import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import elevateLogo from "@/assets/elevate-media-logo.png";

import wakiLogo from "@/assets/waki-logo.png";
import onspotLogo from "@/assets/onspot-logo.png";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building, TreePine, Users, TrendingUp, Target, Zap, Eye, Monitor } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { useState, useEffect } from "react";

const Partners = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const indoorPartners = [
    {
      id: "tableads",
      name: "TableAds",
      logo: "/images/5b702a1b-0694-48a1-9c09-9a077bc3c36b.png",
      description: t('tableadsDesc'),
      isComingSoon: true
    },
    {
      id: "alan",
      name: "Alan",
      logo: "/images/48ff5552-81f9-403d-8375-6b7792006fcb.png",
      description: t('alanDesc')
    },
    {
      id: "waki",
      name: "Waki",
      logo: wakiLogo,
      description: t('wakiDesc')
    },
    {
      id: "elevate-media",
      name: "Elevate Media",
      logo: elevateLogo,
      description: t('elevateMediaDesc'),
      isComingSoon: true
    },
    {
      id: "onspot",
      name: "On Spot",
      logo: onspotLogo,
      description: t('onspotDesc')
    }
  ].sort((a, b) => Number(a.isComingSoon ?? false) - Number(b.isComingSoon ?? false));

  const outdoorPartners = [
    {
      id: "attention",
      name: "Attention",
      logo: "/images/667636f4-bf87-42f0-977e-60656eaaa418.png",
      description: t('attentionDesc')
    },
    {
      id: "coming-soon",
      name: "Coming Soon",
      logo: "/images/938751d5-f09f-48b7-a31c-7b5776f38c5d.png",
      description: t('comingSoonDesc'),
      isComingSoon: true
    }
  ].sort((a, b) => Number(a.isComingSoon ?? false) - Number(b.isComingSoon ?? false));

  const stats = [
    { icon: Users, value: "6", label: t('mediaPartners'), delay: "0ms" },
    { icon: Monitor, value: "4500+", label: t('digitalScreens'), delay: "200ms" },
    { icon: Eye, value: "4.8M+", label: t('dailyViews'), delay: "400ms" },
    { icon: TrendingUp, value: "95%", label: t('clientSatisfaction'), delay: "600ms" }
  ];

  const benefits = [
    {
      icon: Target,
      title: t('targetedAudience'),
      description: t('targetedAudienceDesc'),
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconBg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      borderColor: "border-blue-200"
    },
    {
      icon: TrendingUp,
      title: t('performanceMetrics'),
      description: t('performanceMetricsDesc'),
      gradient: "from-purple-500/20 to-pink-500/20",
      iconBg: "bg-gradient-to-r from-purple-500 to-pink-500",
      borderColor: "border-purple-200"
    },
    {
      icon: Zap,
      title: t('quickSetup'),
      description: t('quickSetupDesc'),
      gradient: "from-emerald-500/20 to-teal-500/20",
      iconBg: "bg-gradient-to-r from-emerald-500 to-teal-500",
      borderColor: "border-emerald-200"
    }
  ];

  const PartnerCard = ({
    partner,
    type
  }: {
    partner: { id: string; name: string; logo: string; description: string; isComingSoon?: boolean };
    type: "indoor" | "outdoor";
  }) => (
    <div
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-border/40 bg-card shadow-md hover:shadow-xl transition-all duration-400 hover:-translate-y-1 ${partner.isComingSoon ? 'cursor-default' : 'cursor-pointer'}`}
      onClick={() => !partner.isComingSoon && navigate(`/partners/${partner.id}`)}
    >
      {/* Gradient accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-primary via-secondary to-primary flex-shrink-0" />

      {/* Logo frame */}
      <div className="relative mx-4 mt-4 h-36 rounded-xl bg-muted/30 border border-border/30 flex items-center justify-center p-4 overflow-hidden">
        {/* Subtle inner glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 rounded-xl" />
        <img
          src={partner.logo}
          alt={`${partner.name} logo`}
          className={`relative z-10 max-h-24 max-w-full w-auto object-contain transition-transform duration-300 group-hover:scale-105 ${partner.isComingSoon ? 'grayscale opacity-50' : ''}`}
          style={{ imageRendering: '-webkit-optimize-contrast', transform: 'translateZ(0)' }}
        />
        {/* Partner type chip — bottom left */}
        <span className="absolute bottom-2 left-2 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-primary/10 text-primary border-primary/20">
          {type === 'indoor' ? <Building className="w-2.5 h-2.5" /> : <TreePine className="w-2.5 h-2.5" />}
          {type === 'indoor' ? t('indoor') : t('outdoor')}
        </span>
        {/* Coming soon badge — top right */}
        {partner.isComingSoon && (
          <span className="absolute top-2 right-2 z-20 bg-secondary text-secondary-foreground px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide shadow-sm">
            {t('comingSoon')}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 px-4 pt-3 pb-4 gap-3">
        <div>
          <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors duration-200 mb-1">
            {partner.name}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
            {partner.description}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-auto pt-1">
          {partner.isComingSoon ? (
            <button
              disabled
              className="w-full py-2 rounded-xl text-sm font-medium bg-muted/60 text-muted-foreground cursor-not-allowed border border-border/40"
            >
              {t('stayTuned')}
            </button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full rounded-xl border-primary/30 text-primary hover:bg-gradient-to-r hover:from-primary hover:to-secondary hover:text-white hover:border-transparent transition-all duration-300"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/partners/${partner.id}`);
              }}
            >
              {t('learnMore')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Media Partners — Fusion Media DOOH Network"
        description="Explore the Fusion Media partner network — indoor and outdoor DOOH operators powering 4,600+ digital screens across Saudi Arabia."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-tour="partners-hero">
          <PageHero
            eyebrow={t('premiumMediaPartnerNetwork')}
            title={t('ourMediaPartners')}
            subtitle={t('partnerDescription')}
            centered
            ctas={[{ label: t('startAdvertising'), to: '/campaign' }]}
          />
        </div>

        {/* Stats Section */}
        <div className="relative mb-12 md:mb-24 p-5 md:p-8 lg:p-12 rounded-3xl border border-border/50 backdrop-blur-sm bg-gradient-to-br from-background/90 via-background/80 dark:from-card/90 dark:via-card/80 to-primary/5 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 rounded-3xl"></div>
          <div className="relative z-10">
            <div className="text-center mb-6 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
                {t('ourNetworkImpact')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('connectingBrands')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-3 md:gap-8 md:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div
                    key={index}
                    className="text-center group transition-all duration-500 hover:scale-105 p-4 md:p-6 rounded-2xl bg-background/50 dark:bg-card/50 backdrop-blur-sm border border-border/30 hover:shadow-lg"
                    style={{ animationDelay: stat.delay }}
                  >
                    <div className="w-11 h-11 md:w-16 md:h-16 bg-gradient-to-r from-primary to-secondary rounded-2xl flex items-center justify-center mb-3 md:mb-6 mx-auto group-hover:shadow-lg transition-all duration-300 group-hover:scale-110">
                      <IconComponent className="text-white w-6 h-6 md:w-8 md:h-8" />
                    </div>
                    <h4 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300">
                      {stat.value}
                    </h4>
                    <p className="text-xs md:text-base text-muted-foreground font-medium">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Partners Tabs Section */}
        <div className="mb-12 md:mb-24" data-tour="partners-grid">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
              {t('exploreOurPartners')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('explorePartnersDesc')}
            </p>
          </div>

          <Tabs defaultValue="indoor" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="grid w-full max-w-2xl grid-cols-2 h-12 sm:h-16 p-2 bg-gradient-to-r from-background/90 via-background/95 dark:from-card/90 dark:via-card/95 to-background/90 dark:to-card/90 backdrop-blur-md border border-border/40 shadow-lg rounded-2xl gap-2 sm:gap-4">
                <TabsTrigger 
                  value="indoor" 
                  className="flex items-center gap-2 sm:gap-3 h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 hover:bg-primary/5"
                >
                  <Building className="h-5 w-5" />
                  <span>{t('indoor')}</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="outdoor" 
                  className="flex items-center gap-2 sm:gap-3 h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-secondary data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 hover:bg-primary/5"
                >
                  <TreePine className="h-5 w-5" />
                  <span>{t('outdoor')}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Indoor Partners */}
            <TabsContent value="indoor" className="animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {indoorPartners.map((partner) => (
                  <PartnerCard key={partner.id} partner={partner} type="indoor" />
                ))}
              </div>
            </TabsContent>

            {/* Outdoor Partners */}
            <TabsContent value="outdoor" className="animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {outdoorPartners.map((partner) => (
                  <PartnerCard key={partner.id} partner={partner} type="outdoor" />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Benefits Section */}
        <div className="mb-12 md:mb-24">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
              {t('whyChoosePartners')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('whyChoosePartnersDesc')}
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-3">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className={`group relative p-5 md:p-8 rounded-2xl border backdrop-blur-sm transition-all duration-500 hover:shadow-xl hover:-translate-y-2 bg-gradient-to-br ${benefit.gradient} ${benefit.borderColor}`}
                  style={{ animationDelay: `${800 + index * 200}ms` }}
                >
                  <div className="absolute inset-0 bg-background/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl"></div>
                  <div className="relative z-10 text-center">
                    <div className={`w-14 h-14 md:w-20 md:h-20 ${benefit.iconBg} rounded-2xl flex items-center justify-center mb-4 md:mb-6 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="text-white w-6 h-6 md:w-10 md:h-10" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div data-tour="partners-cta" className="relative mb-12 md:mb-20 p-5 md:p-8 lg:p-12 rounded-3xl border border-border/50 backdrop-blur-sm bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/10 shadow-xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 rounded-3xl"></div>
          <div className="absolute top-4 right-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-4 left-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl"></div>
          
          <div className="relative z-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              {t('readyToReach')}
            </h2>
            <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('startYourDigital')} {t('andReachYour')}
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/campaign')}
                className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white px-6 py-3 text-base md:px-8 md:py-4 md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                {t('createCampaign')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partners;

